import PhoneStage from "@/components/kit/PhoneStage";
import RealScreen from "@/components/kit/RealScreen";
import FloatingChip from "@/components/kit/FloatingChip";
import { useMotionTier } from "@/lib/motionTier";
import { FILM_PILL, HERO_FILMS, MANNEQUIN_FILMS } from "@/data/mannequinFilms";

/**
 * The home hero's visual: two mannequin films on two overlapping glass phones, the squat in front and the badminton behind it, up and to the right.
 * Both loop; the front phone speaks by itself once the browser allows it (RealScreen, lib/autoSound.js), the back one only when tapped; each has its own "Tap for sound" pill (the coach's real recorded voice), and tapping one silences the other (RealScreen keeps one speaker).
 * A faceless 3D mannequin, no real person; each film draws its own "Illustrated example" label, and the cards say only what that film shows (their words,
 * films and credits live in data/mannequinFilms.js; the CC BY credits are in the site footer). Re-render: frontend/tools/art/mannequin/README.md.
 *
 * Sizing: the box is as wide as the column (up to 30 rem), never taller than the window allows, and both phones are percentages of it, so the pair
 * scales together at every width. The front phone's cards hang off its left edge; the back phone's sit in the free corner above the front phone
 * (they are drawn here, over both phones, because a card inside the back phone would go under the front one).
 */
const SLOTS = [
  // the front phone: the squat, standing at the bottom left
  { film: HERO_FILMS[0], box: "left-0 bottom-0 w-[62.5%] z-20", tilt: { x: 4, y: -10 }, glow: true, pick: [0, 1],
    pos: ["-left-8 top-[33%] lg:-left-12", "-left-5 top-[59%] lg:-left-9"], z: [90, 110] },
  // the back phone: the badminton, up and to the right; its cards are over both, in the corner the front phone leaves free
  { film: HERO_FILMS[1], box: "right-0 top-0 w-[52%] z-10", tilt: { x: 4, y: -6 }, glow: false, pick: [1, 2],
    pos: ["left-[3%] top-0", "left-0 top-[10%]"], z: [100, 70], over: true },
];

const cardsFor = (s) => s.pick.map((idx, i) => {
  const [icon, title, sub] = MANNEQUIN_FILMS[s.film].chips[idx];
  return { icon, title, sub, z: s.z[i], float: i ? "b" : "a", position: s.pos[i], mobile: true };
});

export default function HeroPhone() {
  const still = useMotionTier() === "static";
  return (
    <div className="relative w-full mx-auto lg:mx-0 lg:ml-auto" style={{ aspectRatio: "69 / 100", maxWidth: "min(30rem, calc((100svh - 12.5rem) * 0.69))" }} data-testid="hero-phone">
      {SLOTS.map((s) => {
        const f = MANNEQUIN_FILMS[s.film];
        return (
          <div key={s.film} className={`absolute ${s.box}`} data-testid={`hero-phone-${s.film}`}>
            <PhoneStage size="tall" tilt={s.tilt} glow={s.glow} chips={s.over ? [] : cardsFor(s)} className="!w-full">
              <RealScreen video={f} aspect="9 / 16" fit="cover" sound autoSound={!s.over} soundClass={FILM_PILL} />
            </PhoneStage>
          </div>
        );
      })}
      {SLOTS.filter((s) => s.over).flatMap(cardsFor).map((c, i) => (
        <FloatingChip key={c.title} {...c} z={0} className="z-30" float={still ? null : c.float} delay={still ? undefined : 600 + i * 80} />
      ))}
    </div>
  );
}
